// Unified App View Mode State
// Single source of truth for the entire app's view state

import { signal, computed } from '@preact/signals'
import { safeLocal } from '../utils/safeStorage.js'

/**
 * Main view mode signal
 * Defines what the user is currently viewing
 */
export const appViewMode = signal('cells-grid')
// Possible values:
// - 'cells-grid'           → Grid view with cell cards
// - 'cells-graph'          → Graph (customizable chart, flexible axis selection)
// - 'cells-table'          → Table view
// - 'cells-single'         → Single cell detail (overlay)
// - 'benchmarking-score'   → Score plot
// - 'benchmarking-my'      → My benchmarking plot
// - 'radar-overview'       → Technology Radar overview (3 story cards)
// - 'radar-section'        → Technology Radar section detail (single plot view)

/**
 * Selected cell when in single cell mode
 */
export const selectedCellId = signal(null)
export const selectedCellSlug = signal(null)

/**
 * Active section in single cell view
 */
export const cellSection = signal('factsheet')
// Possible values: 'factsheet', 'electrical', 'thermal', 'mechanical', 'chemical', 'quality'

/**
 * Computed values for convenience
 */
export const isCellsMode = computed(() => appViewMode.value.startsWith('cells-'))
export const isBenchmarkingMode = computed(() => appViewMode.value.startsWith('benchmarking-'))
export const isRadarMode = computed(() => appViewMode.value.startsWith('radar-'))
export const isSingleCellMode = computed(() => appViewMode.value === 'single-cell')
export const isGridMode = computed(() => ['cells-grid', 'cells-graph', 'cells-table'].includes(appViewMode.value))

/**
 * Actions to change view mode
 * NOTE: These functions ONLY update state, NOT the URL
 * URL updates are handled by components using wouter navigate()
 */
export function switchToGrid() {
  appViewMode.value = 'cells-grid'
  selectedCellId.value = null
  selectedCellSlug.value = null
}

export function switchToRagone() {
  appViewMode.value = 'cells-ragone'
}

export function switchToTable() {
  appViewMode.value = 'cells-table'
}

export function openSingleCell(cellId, slug) {
  selectedCellId.value = cellId
  selectedCellSlug.value = slug
  appViewMode.value = 'cells-single'
  cellSection.value = 'factsheet' // Reset to default section
}

export function closeSingleCell() {
  const previousView = safeLocal.get('previousCellsView') || 'cells-grid'
  appViewMode.value = previousView
  selectedCellId.value = null
  selectedCellSlug.value = null
}

export function switchToBenchmarking(subView = 'score') {
  appViewMode.value = `benchmarking-${subView}`
  // NOTE: URL update handled by components using wouter navigate()
}

export function switchToRadar(subView = 'overview') {
  appViewMode.value = `radar-${subView}`
}

export function switchSection(sectionId) {
  cellSection.value = sectionId
}

/**
 * Initialize from URL query params on load
 * This is the ONLY function that reads the URL to set state
 */
export function initFromURL() {
  const url = new URL(window.location)
  const viewParam = url.searchParams.get('view')
  const path = window.location.pathname
  
  console.log('[appViewMode] initFromURL called')
  console.log('[appViewMode] Current URL:', url.href)
  console.log('[appViewMode] Path:', path)
  console.log('[appViewMode] View param:', viewParam)
  console.log('[appViewMode] Current appViewMode:', appViewMode.value)
  
  // Explorer mode from ?view=grid/graph/table
  if ((path === '/explorer' || path === '/cells') && viewParam) {
    if (['grid', 'graph', 'table'].includes(viewParam)) {
      console.log('[appViewMode] Setting explorer mode:', `cells-${viewParam}`)
      appViewMode.value = `cells-${viewParam}`
    }
  }
  
  // Benchmarking mode from ?view=score/my/free
  if ((path.startsWith('/cell-selection') || path.startsWith('/benchmarking')) && viewParam) {
    if (['score', 'my', 'free'].includes(viewParam)) {
      console.log('[appViewMode] Setting benchmarking mode:', `benchmarking-${viewParam}`)
      appViewMode.value = `benchmarking-${viewParam}`
    }
  }
  
  // Legacy: Support old hashtags for backwards compatibility
  const hash = window.location.hash.slice(1)
  if (hash && ['score', 'my', 'free'].includes(hash)) {
    console.log('[appViewMode] Migrating from hash:', hash)
    appViewMode.value = `benchmarking-${hash}`
    // Migrate to query param
    url.searchParams.set('view', hash)
    window.location.hash = '' // Clear hash
    window.history.replaceState({}, '', url)
  }
  
  console.log('[appViewMode] Final appViewMode after init:', appViewMode.value)
}

/**
 * Save previous view when entering single cell mode
 */
export function savePreviousView() {
  if (isGridMode.value) {
    safeLocal.set('previousCellsView', appViewMode.value)
  }
}

// Auto-save previous view when switching to single cell
import { effect } from '@preact/signals'
effect(() => {
  if (isSingleCellMode.value) {
    // Don't overwrite if already in single cell mode
    return
  }
  if (isGridMode.value) {
    safeLocal.set('previousCellsView', appViewMode.value)
  }
})
